// react
import { Suspense, useMemo, useState } from 'react';
// redux
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate, useSearch } from '@tanstack/react-router';

// material ui icons
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import Co2RoundedIcon from '@mui/icons-material/Co2Rounded';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SolarPowerRoundedIcon from '@mui/icons-material/SolarPowerRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import {
  useDurationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetQuery,
  useHourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetQuery,
} from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';
import {
  useConsumptionDataPortfolioConsumptionDataAnalysisTypeAccountIdMeterIdGetQuery,
  useGetMonthlyDataMonthlyDataAnalysisTypeAccountIdMeterIdGetQuery,
} from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisData';
import { useGetOccupancyOccupancyAnalysisTypeRecalcAccountIdMeterIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';

// shared components
import AzureStyleDateRangeSelector from '@/components/ui/datetime/AzureStyleDateRangeSelector';

// feature-hooks
import { useDayRangePresets } from '@/features/energyxplorer/hooks/useDayRangePresets';

// components
import OccupancyDaysCard from '@/features/energyxplorer/portfolio/components/datagrid/cells/OccupancyDaysCard';
import DurationAnalysisDialog from '@/features/energyxplorer/portfolio/components/meter-drawer/charts/DurationAnalysisDialog';
import HourAnalysisDialog from '@/features/energyxplorer/portfolio/components/meter-drawer/charts/HourAnalysisDialog';
import PortfolioAnalysisDataChart from '@/features/energyxplorer/portfolio/components/meter-drawer/charts/PortfolioAnalysisDataChart';
import PortfolioAnalysisGroupedColumnChart from '@/features/energyxplorer/portfolio/components/meter-drawer/charts/PortfolioAnalysisGroupedColumnChart';
import DataIntegrityButton from '@/features/energyxplorer/portfolio/components/meter-drawer/data-integrity/DataIntegrityButton';
import type { AnalysisType } from '@/features/energyxplorer/types';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { downLoadCsvFile } from '@/utils/export';
import { formatNumber } from '@/utils/formatters';

import { DateTime } from 'luxon';

interface ValueDisplayProps {
  label?: string;
  value: string | number | null | undefined;
  icon?: React.ReactNode;
  unit?: string | null;
  trend?: number | null;
}

const getTrendColor = (trend: number) => {
  if (trend >= 10) return 'error' as const;
  if (trend >= 2) return 'warning' as const;
  if (trend > -2) return 'default' as const;
  return 'success' as const;
};

const ValueDisplay = ({ label, value, unit, trend }: ValueDisplayProps) => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <Box sx={{ ml: 1 }}>
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" color="textPrimary">
          {value} {unit}
        </Typography>
        {trend != null && (
          <Chip
            size="small"
            color={getTrendColor(trend)}
            label={`${trend > 0 ? '+' : ''}${trend.toFixed(1)}%`}
            sx={{ fontWeight: 'bold' }}
          />
        )}
      </Box>
    </Box>
  </Box>
);

interface MeterChartPanelProps {
  meter: Record<string, unknown>;
  analysisType: AnalysisType;
}

const MeterChartPanel = ({ meter, analysisType }: MeterChartPanelProps) => {
  const { t } = useTranslation([
    `portfolio-analysis_${analysisType}`,
    'common',
    'energyxplorer',
  ]);
  const account = useSelector(getSelectedAccount);
  const dayRangePresets = useDayRangePresets();

  const search = useSearch({ strict: false });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nav = useNavigate() as any;
  const selectedChart = (search.selectedChart as string) || 'consumption';
  const setSelectedChart = (chart: string) =>
    nav({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        selectedChart: chart,
      }),
    });

  const isSolarPanel = meter?.type_of_mp === 'PV' && analysisType === 'power';

  const meterId = (() => {
    if (analysisType === 'power') {
      return (meter.metering_point_id ?? meter.MeteringPointId) as
        number | undefined;
    }
    const dataStream = meter.data_stream as Record<string, unknown> | undefined;
    return (dataStream?.unit_id ?? meter.UnitId) as number | undefined;
  })();

  const [selectedDateRange, setSelectedDateRange] = useState<{
    start: number;
    end: number;
  }>({
    start: DateTime.now().minus({ weeks: 4 }).startOf('day').toMillis(),
    end: DateTime.now().startOf('day').toMillis(),
  });

  const [durationAnalysisFilterOptions, setDurationAnalysisFilterOptions] =
    useState<Record<string, boolean | null | undefined>>({});
  const [hourAnalysisFilterOptions, setHourAnalysisFilterOptions] = useState<
    Record<string, boolean | null | undefined>
  >({});

  const { data: occupancyData } =
    useGetOccupancyOccupancyAnalysisTypeRecalcAccountIdMeterIdGetQuery(
      {
        recalcAccountId: account?.AccountId,
        meterId: meterId as number,
        analysisType,
      },
      { skip: !account?.AccountId || !meterId },
    );

  const { data: durationAnalysisData, isFetching: isDurationAnalysisFetching } =
    useDurationAnalysisPortfolioDurationAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetQuery(
      {
        analysisType,
        accountId: account?.AccountId,
        meterId: meterId as number,
        startTimestamp: new Date(selectedDateRange.start).toISOString(),
        endTimestamp: new Date(selectedDateRange.end).toISOString(),
        activePeriodFilter: durationAnalysisFilterOptions.activePeriodFilter,
        holidaysFilter: durationAnalysisFilterOptions.activeHolidaysFilter,
      },
      {
        skip: !meterId || analysisType !== 'power' || isSolarPanel,
      },
    );

  const { data: hourAnalysisData, isFetching: isHourAnalysisFetching } =
    useHourAnalysisPortfolioHourAnalysisPortfolioAnalysisTypeAccountIdMeterIdGetQuery(
      {
        analysisType,
        accountId: account?.AccountId,
        meterId: meterId as number,
        startTimestamp: new Date(selectedDateRange.start).toISOString(),
        endTimestamp: new Date(selectedDateRange.end).toISOString(),
        activeDaysFilter: hourAnalysisFilterOptions.activeDaysFilter,
        holidaysFilter: hourAnalysisFilterOptions.activeHolidaysFilter,
      },
      {
        skip:
          !meterId ||
          isSolarPanel ||
          (meter?.data_stream as Record<string, unknown> | undefined)
            ?.aggregate_type === 'Day',
      },
    );

  const { data: chartData, isFetching: isChartFetching } =
    useGetMonthlyDataMonthlyDataAnalysisTypeAccountIdMeterIdGetQuery(
      {
        analysisType,
        accountId: account?.AccountId,
        meterId: meterId as number,
      },
      { skip: !meterId },
    );

  const { data: unitData, isFetching: isUnitFetching } =
    useConsumptionDataPortfolioConsumptionDataAnalysisTypeAccountIdMeterIdGetQuery(
      {
        analysisType,
        accountId: account?.AccountId,
        meterId: meterId as number,
        startTimestamp: new Date(selectedDateRange.start).toISOString(),
        endTimestamp: new Date(selectedDateRange.end).toISOString(),
      },
      { skip: !meterId },
    );

  const mergedMeter = { ...meter, ...(occupancyData ?? {}) };

  const charts = (() => {
    if (isSolarPanel) {
      return [{ chartName: 'consumption' }, { chartName: 'yearOnYear' }];
    }
    if (analysisType === 'power') {
      return [
        { chartName: 'consumption' },
        { chartName: 'yearOnYear' },
        { chartName: 'durationAnalysis' },
        { chartName: 'hourAnalysis' },
      ];
    }
    if (
      (meter?.data_stream as Record<string, unknown> | null | undefined)
        ?.aggregate_type === 'Day'
    ) {
      return [
        { chartName: 'consumption' },
        { chartName: 'yearOnYear' },
        { chartName: 'hourAnalysis', disabled: true },
      ];
    }
    return [
      { chartName: 'consumption' },
      { chartName: 'yearOnYear' },
      { chartName: 'hourAnalysis' },
    ];
  })();

  const chartProps = useMemo(() => {
    switch (selectedChart) {
      case 'durationAnalysis':
        return {
          open: !!meterId,
          onClose: () => {},
          row: meter,
          data: durationAnalysisData,
          isLoading: isDurationAnalysisFetching,
          setFilterOptions: setDurationAnalysisFilterOptions,
        };
      case 'hourAnalysis':
        return {
          open: !!meterId,
          onClose: () => {},
          row: meter,
          data: hourAnalysisData,
          isLoading: isHourAnalysisFetching,
          setFilterOptions: setHourAnalysisFilterOptions,
          unit: analysisType === 'water' ? 'm³' : 'kWh',
        };
      case 'consumption':
        return {
          address: meter?.address,
          data: unitData,
          isLoading: isUnitFetching,
          unit: analysisType === 'water' ? 'm³' : 'kWh',
        };
      case 'yearOnYear':
        return {
          address: meter?.address,
          data: chartData,
          isLoading: isChartFetching,
          unit: analysisType === 'water' ? 'm³' : 'kWh',
        };
      default:
        return {};
    }
  }, [
    selectedChart,
    meterId,
    meter,
    durationAnalysisData,
    isDurationAnalysisFetching,
    hourAnalysisData,
    isHourAnalysisFetching,
    unitData,
    isUnitFetching,
    analysisType,
    chartData,
    isChartFetching,
  ]);

  const Chart = useMemo(() => {
    switch (selectedChart) {
      case 'durationAnalysis':
        return DurationAnalysisDialog;
      case 'hourAnalysis':
        return HourAnalysisDialog;
      case 'consumption':
        return PortfolioAnalysisDataChart;
      case 'yearOnYear':
        return PortfolioAnalysisGroupedColumnChart;
      default:
        return null;
    }
  }, [selectedChart]);

  const yoyPct =
    meter.yoy_ratio != null ? ((meter.yoy_ratio as number) - 1) * 100 : null;

  if (isSolarPanel && !meter?.data_stream_net_production) {
    return (
      <Box
        sx={{
          height: '100%',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 3,
        }}
      >
        <SolarPowerRoundedIcon
          sx={{
            fontSize: 64,
            color: 'text.disabled',
            mb: 1.5,
          }}
        />

        <Typography variant="subtitle1" color="text.secondary">
          {t('drawer.cards.graphData.solarPanelNoData')}
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2} alignItems="stretch">
      {/* Chart panel */}
      <Grid
        size={{
          xs: 12,
          md: 9,
        }}
      >
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flexGrow: 1 }}>
            <Box
              display="flex"
              minHeight="65px"
              flexDirection="column"
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <ToggleButtonGroup
                value={selectedChart}
                exclusive
                onChange={(_, newChart) => {
                  if (newChart !== null) setSelectedChart(newChart);
                }}
                sx={{ mr: 2 }}
              >
                {charts.map((chart, index) => {
                  if (chart.disabled) {
                    return (
                      <Tooltip
                        key={index}
                        title={t(
                          `drawer.cards.graphData.charts.${chart.chartName}.disabledTooltipBecauseDayAggregate`,
                        )}
                      >
                        <span>
                          <ToggleButton value={chart.chartName} disabled>
                            {t(
                              `drawer.cards.graphData.charts.${chart.chartName}.name`,
                            )}
                          </ToggleButton>
                        </span>
                      </Tooltip>
                    );
                  }
                  return (
                    <ToggleButton
                      key={index}
                      value={chart.chartName}
                      sx={{ px: 1 }}
                    >
                      {t(
                        `drawer.cards.graphData.charts.${chart.chartName}.name`,
                      )}
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>

              {selectedChart !== 'yearOnYear' && (
                <Box display="flex" flexDirection="row">
                  <Box my="auto">
                    <AzureStyleDateRangeSelector
                      from={DateTime.fromMillis(
                        selectedDateRange.start,
                      ).setZone('Europe/Copenhagen')}
                      to={DateTime.fromMillis(selectedDateRange.end).setZone(
                        'Europe/Copenhagen',
                      )}
                      presets={dayRangePresets}
                      includeTime={false}
                      disableFuture
                      onRangeChange={({ from, to }) => {
                        const now = DateTime.now();
                        const end = to
                          .setZone('Europe/Copenhagen')
                          .endOf('day');
                        setSelectedDateRange({
                          start: from
                            .setZone('Europe/Copenhagen')
                            .startOf('day')
                            .toMillis(),
                          end: (end > now ? now : end).toMillis(),
                        });
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignContent: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      padding: 1,
                    }}
                  >
                    <Tooltip
                      title={t('drawer.cards.graphData.exportButtonTooltip')}
                    >
                      <IconButton
                        size="large"
                        onClick={() =>
                          downLoadCsvFile(
                            unitData?.Data,
                            `${meter.address as string}_data.csv`,
                          )
                        }
                      >
                        <FileDownloadIcon fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </Box>

            {Chart && (
              <Box>
                <Suspense fallback={<CircularProgress />}>
                  <Chart {...(chartProps as Record<string, unknown>)} />
                </Suspense>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Sidebar panel */}
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardContent
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {isSolarPanel ? (
              <>
                <ValueDisplay
                  label={t('drawer.cards.pv_net_production_year.header')}
                  value={formatNumber(meter.pv_net_production_year)}
                  icon={<BoltRoundedIcon sx={{ mr: 1 }} />}
                  unit={t('columns.pv_net_production_year.label')}
                />
                <Divider />
                <ValueDisplay
                  label={t('drawer.cards.pv_supplied_to_grid_year.header')}
                  value={formatNumber(meter.pv_supplied_to_grid_year)}
                  icon={<BoltRoundedIcon sx={{ mr: 1 }} />}
                  unit={t('columns.pv_supplied_to_grid_year.label')}
                />
                <Divider />
                <ValueDisplay
                  label={t('drawer.cards.pv_co2e_savings_year.header')}
                  value={formatNumber(meter.pv_co2e_savings_year)}
                  icon={<Co2RoundedIcon sx={{ mr: 1 }} />}
                  unit={t('columns.pv_co2e_savings_year.label')}
                />
              </>
            ) : (
              <>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ ml: 1 }}
                >
                  {t('matrikel.consumption_title', {
                    ns: 'energyxplorer',
                    defaultValue: 'Forbrug og udvikling de sidste 12 måneder',
                  })}
                </Typography>
                <ValueDisplay
                  value={formatNumber(meter.total_consumption_year)}
                  unit={t('columns.total_consumption_year.label')}
                  trend={yoyPct}
                />
                <ValueDisplay
                  value={formatNumber(meter.total_co2e_year)}
                  unit={t('columns.total_co2e_year.label')}
                />
                <Divider />

                <ValueDisplay
                  label={t('drawer.cards.activityPatterns.header')}
                  value={
                    <OccupancyDaysCard
                      daysOfWeek={
                        mergedMeter?.ManualOccupancy ??
                        mergedMeter?.CalculatedOccupancy ??
                        mergedMeter?.DefaultOccupancy
                      }
                    />
                  }
                />
                <Divider />
                <ValueDisplay
                  label={t('drawer.cards.dataIntegrity.header')}
                  value={<DataIntegrityButton row={meter} />}
                  icon={<BoltRoundedIcon sx={{ mr: 1 }} />}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default MeterChartPanel;
